import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const OPENING_CLASS = 'pasquini-inspiration__drawer--opening';
const CLOSING_CLASS = 'pasquini-inspiration__drawer--closing';

/**
 * Carousel, look drawer and cart actions for the Pasquini Inspiration section.
 *
 * @extends {Component}
 */
class PasquiniInspirationComponent extends Component {
  /** @type {any} */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    this.#waitForSwiper();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('cancel', this.#handleCancel);
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  /**
   * Called by the Section Rendering API's morph after this component's subtree
   * changes (e.g. a setting toggled in the theme editor). Swiper mutates the
   * slide markup directly (inline transforms, active-slide classes); morph
   * resets that markup to match the freshly rendered HTML, so the carousel
   * must be torn down and rebuilt against the patched DOM.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.#waitForSwiper();
  }

  /**
   * Handles all section actions through event delegation, including block
   * markup re-rendered by the theme editor.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-inspiration-drawer')) {
      this.#closeDrawer(event.target);
      return;
    }

    const target = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('button') : null
    );
    if (!target || !this.contains(target)) return;

    const drawerId = target.dataset.inspirationOpen;
    if (drawerId) {
      this.#openDrawer(drawerId);
      return;
    }

    if (target.hasAttribute('data-inspiration-close')) {
      const dialog = target.closest('dialog');
      if (dialog) this.#closeDrawer(dialog);
      return;
    }

    if (target.hasAttribute('data-inspiration-size')) {
      this.#selectSize(target);
      return;
    }

    if (target.hasAttribute('data-inspiration-add')) {
      const variantId = target.dataset.variantId;
      if (!variantId) {
        if (target.hasAttribute('data-requires-size')) this.#flagMissingSizes([target]);
        return;
      }

      this.#addItems(
        [
          {
            variantId,
            quantity: Number(target.dataset.quantity) || 1,
          },
        ],
        target
      );
      return;
    }

    if (target.hasAttribute('data-inspiration-add-all')) {
      const drawer = target.closest('[data-inspiration-drawer]');
      if (!drawer) return;

      const addButtons = /** @type {HTMLElement[]} */ (
        Array.from(drawer.querySelectorAll('[data-inspiration-add]:not(:disabled)'))
      );

      const missingSize = addButtons.filter((button) => !button.dataset.variantId && button.hasAttribute('data-requires-size'));
      if (missingSize.length > 0) {
        this.#flagMissingSizes(missingSize);
        return;
      }

      const items = addButtons
        .map((button) => ({
          variantId: button.dataset.variantId || '',
          quantity: Number(button.dataset.quantity) || 1,
        }))
        .filter((item) => item.variantId);

      this.#addItems(items, target);
      return;
    }

  };

  /**
   * Marks the clicked size as selected and hands its variant to the product's add button.
   * @param {HTMLElement} sizeButton
   */
  #selectSize(sizeButton) {
    const product = sizeButton.closest('.pasquini-inspiration__product');
    const group = sizeButton.closest('[data-inspiration-sizes]');
    const addButton = /** @type {HTMLElement | null} */ (product?.querySelector('[data-inspiration-add]') ?? null);
    if (!group || !addButton) return;

    for (const option of group.querySelectorAll('[data-inspiration-size]')) {
      option.setAttribute('aria-checked', String(option === sizeButton));
    }
    group.classList.remove('pasquini-inspiration__sizes--error');

    addButton.dataset.variantId = sizeButton.dataset.variantId || '';
    addButton.dataset.quantity = sizeButton.dataset.quantity || '1';

    const status = sizeButton.closest('[data-inspiration-drawer]')?.querySelector('[data-inspiration-status]');
    if (status) status.textContent = '';
  }

  /**
   * Highlights the size pickers that still need a choice.
   * @param {HTMLElement[]} addButtons
   */
  #flagMissingSizes(addButtons) {
    for (const button of addButtons) {
      button
        .closest('.pasquini-inspiration__product')
        ?.querySelector('[data-inspiration-sizes]')
        ?.classList.add('pasquini-inspiration__sizes--error');
    }

    const drawer = addButtons[0]?.closest('[data-inspiration-drawer]');
    const status = drawer?.querySelector('[data-inspiration-status]');
    if (status) {
      status.textContent = addButtons.length > 1 ? 'Selecione o tamanho de todos os produtos.' : 'Selecione um tamanho.';
    }
  }

  /** @param {Event} event */
  #handleCancel = (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-inspiration-drawer')) {
      event.preventDefault();
      this.#closeDrawer(event.target);
    }
  };

  /** @param {string} drawerId */
  #openDrawer(drawerId) {
    const drawer = this.querySelector(`#${CSS.escape(drawerId)}`);
    if (!(drawer instanceof HTMLDialogElement)) return;

    for (const openDrawer of this.querySelectorAll('dialog[open]')) {
      if (openDrawer !== drawer && openDrawer instanceof HTMLDialogElement) this.#closeDrawer(openDrawer);
    }

    if (drawer.open) return;

    drawer.classList.remove(CLOSING_CLASS);
    drawer.classList.add(OPENING_CLASS);
    drawer.showModal();
    onAnimationEnd(drawer, () => drawer.classList.remove(OPENING_CLASS), { subtree: false });
  }

  /**
   * Plays the closing animation before actually closing the dialog.
   * @param {HTMLDialogElement} drawer
   */
  async #closeDrawer(drawer) {
    if (!drawer.open) return;

    drawer.classList.remove(OPENING_CLASS);
    drawer.classList.add(CLOSING_CLASS);
    await onAnimationEnd(drawer, undefined, { subtree: false });
    drawer.classList.remove(CLOSING_CLASS);
    drawer.close();
  }

  /** @param {number} [attempt] */
  #waitForSwiper(attempt = 0) {
    if (typeof window.Swiper !== 'undefined') {
      this.#initializeSwiper();
      return;
    }

    if (attempt >= 40) return;
    window.setTimeout(() => this.#waitForSwiper(attempt + 1), 50);
  }

  #initializeSwiper() {
    const swiperElement = this.querySelector('[data-inspiration-swiper]');
    if (!swiperElement || swiperElement.querySelectorAll('.swiper-slide').length === 0) return;

    const previousButton = this.querySelector('[data-inspiration-previous]');
    const nextButton = this.querySelector('[data-inspiration-next]');
    const scrollbar = this.querySelector('[data-inspiration-pagination]');

    this.#swiper = new window.Swiper(swiperElement, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 12,
      watchOverflow: true,
      navigation:
        previousButton && nextButton
          ? {
              prevEl: previousButton,
              nextEl: nextButton,
            }
          : undefined,
      scrollbar: scrollbar
        ? {
            el: scrollbar,
            draggable: true,
          }
        : undefined,
      breakpoints: {
        750: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 16,
        },
      },
    });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} rawItems
   * @param {HTMLElement} trigger
   */
  async #addItems(rawItems, trigger) {
    if (rawItems.length === 0) return;

    const itemsByVariant = new Map();
    for (const item of rawItems) {
      itemsByVariant.set(item.variantId, (itemsByVariant.get(item.variantId) || 0) + item.quantity);
    }
    const items = Array.from(itemsByVariant, ([variantId, quantity]) => ({ variantId, quantity }));

    const drawer = trigger.closest('[data-inspiration-drawer]');
    const status = drawer?.querySelector('[data-inspiration-status]');
    const enabledButtons = Array.from(
      drawer?.querySelectorAll('[data-inspiration-add]:not(:disabled), [data-inspiration-add-all]:not(:disabled)') || []
    );

    for (const button of enabledButtons) {
      if (button instanceof HTMLButtonElement) button.disabled = true;
    }
    if (status) status.textContent = 'Adicionando ao carrinho…';

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.variantId);
        formData.append(`items[${index}][quantity]`, String(item.quantity));
      });

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'), (element) =>
        element instanceof HTMLElement ? element.dataset.sectionId : ''
      ).filter(Boolean);
      if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'text/html',
        },
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Não foi possível adicionar os produtos.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Não foi possível atualizar o carrinho.');

      const cart = await cartResponse.json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'pasquini-inspiration',
          sourceId: this.id,
          itemCount: items.reduce((total, item) => total + item.quantity, 0),
          sections: result.sections,
          didError: false,
        },
      });

      if (status) {
        status.textContent = items.length > 1 ? 'Produtos adicionados ao carrinho.' : 'Produto adicionado ao carrinho.';
      }

      if (drawer instanceof HTMLDialogElement) this.#closeDrawer(drawer);

      const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
        document.querySelector('theme-drawer#cart-drawer')
      );
      requestAnimationFrame(() => {
        if (cartDrawer?.open) {
          cartDrawer.open();
        } else {
          window.location.href = Theme.routes.cart_url || '/cart';
        }
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível adicionar os produtos.';
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );
      if (status) status.textContent = message;
    } finally {
      for (const button of enabledButtons) {
        if (button instanceof HTMLButtonElement) button.disabled = false;
      }
    }
  }
}

if (!customElements.get('pasquini-inspiration-component')) {
  customElements.define('pasquini-inspiration-component', PasquiniInspirationComponent);
}
